import Lightning from '@lightningjs/sdk/src/Lightning';

export interface AdCuePoint {
  start: number;
  end: number;
  played: boolean;
}

const AD_SECOND_OFFSET = 1;
const EARLY_AD_SKIP_THRESHOLD = 0.75;

interface AbstractAdManagerTemplateSpec
  extends Lightning.Component.TemplateSpec {
  cuePoints: Array<AdCuePoint> | undefined;
  getRawTime: () => number;
}

export default abstract class AbstractAdManager
  extends Lightning.Component<AbstractAdManagerTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<AbstractAdManagerTemplateSpec>
{
  abstract readonly adManagerType: 'ima' | 'brightcove';
  protected _cuePoints: Array<AdCuePoint> | undefined;

  getRawTime: () => number = () => 0;

  get cuePoints() {
    return this._cuePoints;
  }

  override _inactive(): void {
    this.getRawTime = () => 0;
  }

  abstract getCuePoint(time: number): AdCuePoint | null;
  abstract getCurrentCuePoint(): AdCuePoint | null;
  abstract getNextCuePoint(): AdCuePoint | null;
  abstract getPreviousCuePoint(time: number): AdCuePoint | null;

  // get the next closest (or in) cue point from the given stream time
  getNextCuePointFrom(streamTime: number): AdCuePoint | null {
    return this._cuePoints?.find(cp => cp.end >= streamTime) ?? null;
  }

  initialize() {
    this._cuePoints = undefined;
  }

  isAdPreroll(): boolean {
    const cue = this.getCurrentCuePoint();
    return !!cue && cue.start === 0;
  }

  shouldSkipAdPod(hasStartedAds: boolean): boolean {
    // Do not skip if user is already watching ads (an ad pod's `played`
    // attribute will be true once an ad pod starts)
    if (hasStartedAds) return false;

    const cuePoint = this.getCurrentCuePoint();
    if (!cuePoint) return false;

    // There is a brief section of time where an ad pod is beginning but the ads
    // state is unset. Check if the cuePoint has already been played
    const playerCurrentTime = this.getRawTime();
    return (
      cuePoint.played && playerCurrentTime <= cuePoint.end - AD_SECOND_OFFSET
    );
  }

  shouldEarlySkipAdPod() {
    // For Brightcove SSAI, as soon as it enters the cuePoints, the ads render.
    // If that ad is previously watched, it renders for a split second and then gets skipped.
    // To avoid this, this function checks if the next cuePoint is starting soon,
    // and skips to the end of the cuePoint.

    const nextCuePoint = this.getNextCuePoint();

    if (nextCuePoint && nextCuePoint.played) {
      const playerCurrentTime = this.getRawTime();
      if (
        nextCuePoint &&
        nextCuePoint.start - playerCurrentTime <= EARLY_AD_SKIP_THRESHOLD
      ) {
        return true;
      }
    }
    return false;
  }

  isPlayerInAdPod(): boolean {
    const cuePoint = this.getCurrentCuePoint();
    if (!cuePoint) return false;

    // Adding a slight offset to ad start time to account for any timing issues
    const cuePointStart = cuePoint.start - AD_SECOND_OFFSET;
    const cuePointEnd = cuePoint.end;
    const playerCurrentTime = this.getRawTime();

    return (
      cuePointStart <= playerCurrentTime && playerCurrentTime < cuePointEnd
    );
  }

  adDuration(): number | null {
    const cue = this.getCurrentCuePoint();
    if (!cue) return null;

    const { start, end } = cue;
    return end - start;
  }

  currentAdTime(): number {
    const cue = this.getCurrentCuePoint();
    if (!cue) return 0;

    const { start, end } = cue;
    const currentTime = this.getRawTime() - start;

    if (end < currentTime) return end;
    return currentTime;
  }

  getRemainingAdTime(): number | null {
    const cue = this.getCurrentCuePoint();
    if (!cue) return null;

    const { end } = cue;
    return end - Math.round(this.getRawTime());
  }

  currentAdTimeRemaining(): number {
    return 0;
  }

  shouldCleanupAdPod(hasStartedAds: boolean): boolean {
    // No need to cleanup ad pod if we are not in an ad state
    if (!hasStartedAds) return false;

    // IMA will occasionally fail to send an AD_BREAK_ENDED event. Verify that
    // we are still playing an ad
    return !this.isPlayerInAdPod();
  }

  /**
   * IMA will sometimes fail to set an ad pod's `played` value. This usually occurs in
   * the `CUEPOINTS_CHANGED` IMA event, which happens at the start of an ad. This method
   * guarantees that upon completing an ad, the ad pod's `played` state will be `true`
   */
  adPodEnded() {
    const cuePoint =
      this.adManagerType === 'brightcove'
        ? this.getPreviousCuePoint(this.getRawTime())
        : this.getCurrentCuePoint();
    if (!cuePoint || !this._cuePoints) return;

    const savedCuePointIndex = this._cuePoints.findIndex(
      cp => cp.start === cuePoint.start && cp.end === cuePoint.end,
    );
    if (savedCuePointIndex === -1) return;

    this._cuePoints[savedCuePointIndex]!.played = true;
    this.signal('cuePointsChanged', this._cuePoints);
  }
}
